import React, { useState } from 'react';
import FileStruct from '../../model/FileStruct';

interface ImageUploadWidgetProps {
    title: string;
    buttonTitle: string;
    onUpload: (files: FileStruct[]) => void;  // Callback function to send base64 images
}

const ImageUploadComponent: React.FC<ImageUploadWidgetProps> = ({ title, buttonTitle, onUpload }) => {
    const [images, setImages] = useState<File[]>([]);
    const [modalImage, setModalImage] = useState<string | null>(null);
    const [base64Images, setBase64Images] = useState<FileStruct[]>([]);

    const compressImage = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            const reader = new FileReader();

            reader.readAsDataURL(file);
            reader.onload = (e) => {
                img.src = e.target?.result as string;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    // Set canvas dimensions to reduce size
                    const maxWidth = 800;  // Adjust as needed
                    const scaleFactor = maxWidth / img.width;
                    canvas.width = maxWidth;
                    canvas.height = img.height * scaleFactor;

                    ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

                    // Compress and convert to base64
                    const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7); // Adjust quality (0.7 = 70%)

                    // Check if the image is still larger than 1MB and adjust as needed
                    const sizeInMB = atob(compressedBase64.split(',')[1]).length / 1e6;
                    if (sizeInMB <= 1) {
                        resolve(compressedBase64);
                    } else {
                        // Re-compress with lower quality if needed
                        resolve(canvas.toDataURL('image/jpeg', 0.5)); // Adjust lower if needed
                    }
                };
                img.onerror = (err) => reject(err);
            };
            reader.onerror = (err) => reject(err);
        });
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        const files = e.target.files;
        if (files) {
            const fileArray = Array.from(files);
            setImages((prev) => [...prev, ...fileArray]);

            var current = base64Images;
            for (const file of fileArray) {
                try {
                    const compressedBase64 = await compressImage(file);
                    const extension = file.name.split('.').pop() || '';
                    const fileStruct: FileStruct = {
                        text: '',
                        type: file.type.replace("image/", ""),
                        path: compressedBase64,
                        extension: "jpeg",
                        name: file.name,
                        pages: 1,
                    };
                    setBase64Images((prev) => [...prev, fileStruct]);
                    current = [...current, fileStruct];
                    onUpload(current);
                } catch (error) {
                    console.error('Error compressing image:', error);
                }
            }
        }
    };

    const openModal = (base64: string) => {
        setModalImage(base64);
    };

    const closeModal = () => {
        setModalImage(null);
    };

    const handleDelete = (index: number) => {
        var current = base64Images;
        current = base64Images.filter((_, i) => i !== index);
        setBase64Images(current);
        setImages(images.filter((_, i) => i !== index));
        onUpload(current);
    };

    return (
        <div style={styles.card}>
            <h3>{title}</h3>
            <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                style={styles.input}
            />

            <div style={styles.imageContainer}>
                {base64Images.map((fileStruct, index) => (
                    <div key={index} style={styles.thumbnail}>
                        <img
                            src={fileStruct.path}
                            alt={`thumbnail-${index}`}
                            style={styles.image}
                            onClick={() => openModal(fileStruct.path)}
                        />
                        <span style={styles.deleteIcon} onClick={() => handleDelete(index)}>
                            &times;
                        </span>
                    </div>
                ))}
            </div>

            {modalImage && (
                <div style={styles.modal} onClick={closeModal}>
                    <img src={modalImage} alt="full-view" style={styles.modalImage} />
                </div>
            )}
        </div>
    );
};

// Styles
const styles = {
    card: {
        maxWidth: '400px',
        margin: '20px auto',
        padding: '20px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column' as 'column',
        alignItems: 'center',
    },
    input: {
        marginBottom: '20px',
    },
    imageContainer: {
        display: 'flex',
        flexWrap: 'wrap' as 'wrap',
        justifyContent: 'center' as 'center',
        width: '100%',
    },
    thumbnail: {
        margin: '10px',
        position: 'relative' as 'relative',
    },
    image: {
        width: '100px',
        height: '100px',
        objectFit: 'cover' as 'cover',
        cursor: 'pointer' as 'pointer',
        borderRadius: '4px',
        border: '1px solid #ddd',
    },
    deleteIcon: {
        position: 'absolute' as 'absolute',
        top: '5px',
        right: '5px',
        backgroundColor: '#ff0000',
        color: '#fff',
        borderRadius: '50%',
        width: '20px',
        height: '20px',
        display: 'flex',
        justifyContent: 'center' as 'center',
        alignItems: 'center' as 'center',
        cursor: 'pointer' as 'pointer',
        fontSize: '16px',
        lineHeight: '20px',
    },
    modal: {
        position: 'fixed' as 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        justifyContent: 'center' as 'center',
        alignItems: 'center' as 'center',
    },
    modalImage: {
        maxWidth: '90%',
        maxHeight: '90%',
    },
    uploadButton: {
        marginTop: '20px',
        padding: '10px 20px',
        backgroundColor: '#007BFF',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer' as 'pointer',
    },
};

export default ImageUploadComponent;
